import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { inject } from '@/rules-management/injections.ts';
import { RULES_REPOSITORY } from '@/rules-management/RulesManagementKeys.ts';
import {
  PROVIDE_RULES_MANAGEMENT,
  provideRulesManagement,
} from '@/rules-management/RulesManagementProvider.ts';
import { useInitOnce } from '@/generic/init';
import { Optional } from '@/generic/functional';
import { Pagination, type Page } from '@/generic/pagination';
import {
  type RuleExtractionId,
  RuleExtractionFailed,
  RuleExtractionInProgress,
} from '@/rules-management/domain/RuleExtraction.ts';
import type { RulesFileToImport } from '@/rules-management/domain/RulesFileToImport.ts';
import type { DraftRule } from '@/rules-management/domain/DraftRule.ts';
import {
  type ExtractionFailure,
  ExtractionStep,
} from '@/rules-management/primary/ExtractionFailure.ts';
import {
  type RuleExtractionTracking,
  type RuleExtractionTrackingToast,
  RuleExtractionTrackingReactContext,
} from '@/rules-management/primary/RuleExtractionTrackingContext.ts';
import { useRuleExtractionPageMountedTracking } from '@/rules-management/primary/UseRuleExtractionPageMountedTracking.ts';
import {
  clearTrackedExtraction,
  loadTrackedExtraction,
  saveTrackedExtraction,
} from '@/rules-management/secondary/RuleExtractionTrackingStorage.ts';

const FIRST_DRAFT_RULES_PAGE = 1;
const DRAFT_RULES_PAGE_SIZE = 30;

export const RuleExtractionTrackingProvider = ({ children }: { children: ReactNode }) => {
  useInitOnce(PROVIDE_RULES_MANAGEMENT, provideRulesManagement);
  const rulesRepository = inject(RULES_REPOSITORY);
  const location = useLocation();

  const [lastFile, setLastFile] = useState<Optional<RulesFileToImport>>(Optional.empty());
  const [visible, setVisible] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [failure, setFailure] = useState<Optional<ExtractionFailure>>(Optional.empty());
  const [drafts, setDrafts] = useState<Optional<Page<DraftRule>>>(Optional.empty());
  const [toastDismissed, setToastDismissed] = useState(false);

  // Tracks whether the rules page is actually mounted, so a background completion only pops the
  // import dialog open if the user is watching; otherwise the toast/indicator informs them and
  // reopening later requires an explicit action (see openImport).
  const { notifyPageMounted, isPageMounted } = useRuleExtractionPageMountedTracking();

  const loadInitialDrafts = useCallback((): Promise<void> => {
    setExtracting(true);
    return rulesRepository
      .listDrafts(Pagination.of({ page: FIRST_DRAFT_RULES_PAGE, pageSize: DRAFT_RULES_PAGE_SIZE }))
      .then((loaded) => {
        setDrafts(Optional.of(loaded));
        rulesRepository.notifyDraftRulesChanged();
        if (isPageMounted()) {
          setVisible(true);
        }
      })
      .catch((error: Error) => {
        setFailure(Optional.of({ error, step: ExtractionStep.LOADING_DRAFTS }));
      })
      .finally(() => setExtracting(false));
  }, [rulesRepository, isPageMounted]);

  const listen = useCallback(
    (id: RuleExtractionId, onSettled?: () => void) => {
      const unsubscribe = rulesRepository.listenExtraction(id)((extraction) => {
        if (extraction instanceof RuleExtractionInProgress) {
          return;
        }
        unsubscribe();
        if (extraction instanceof RuleExtractionFailed) {
          setExtracting(false);
          setFailure(Optional.of({ error: extraction.error, step: ExtractionStep.EXTRACTING }));
          onSettled?.();
          return;
        }
        clearTrackedExtraction();
        void loadInitialDrafts().finally(() => onSettled?.());
      });
    },
    [rulesRepository, loadInitialDrafts],
  );

  useEffect(() => {
    loadTrackedExtraction().ifPresent((id) => {
      setExtracting(true);
      setVisible(false);
      // RulesComponent only mounts once the current-user loader resolves, which can happen after
      // this effect already resumed and possibly completed the tracked extraction. Prime the
      // mounted signal synchronously from the URL so a reload while already on the rules page
      // doesn't race past its own mount notification.
      if (location.pathname.startsWith('/rules')) {
        notifyPageMounted(true);
      }
      listen(id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listen]);

  const extract = (file: RulesFileToImport): Promise<void> => {
    setLastFile(Optional.of(file));
    setVisible(true);
    setExtracting(true);
    setFailure(Optional.empty());
    setDrafts(Optional.empty());
    setToastDismissed(false);

    return rulesRepository
      .extract(file)
      .then(
        (id) =>
          new Promise<void>((resolve) => {
            saveTrackedExtraction(id);
            listen(id, resolve);
          }),
      )
      .catch((error: Error) => {
        setExtracting(false);
        setFailure(Optional.of({ error, step: ExtractionStep.EXTRACTING }));
      });
  };

  const retry = () => {
    const step = failure.map((current) => current.step).orElse(ExtractionStep.EXTRACTING);
    setFailure(Optional.empty());
    if (step === ExtractionStep.LOADING_DRAFTS) {
      void loadInitialDrafts();
      return;
    }
    lastFile.ifPresent(extract);
  };

  const openImport = () => setVisible(true);

  const closeImport = () => {
    setVisible(false);
    setFailure(Optional.empty());
    setDrafts(Optional.empty());
  };

  const continueInBackground = () => setVisible(false);

  const dismissFailure = () => setFailure(Optional.empty());

  const dismissToast = () => setToastDismissed(true);

  const toast: Optional<RuleExtractionTrackingToast> =
    (drafts.isPresent() || failure.isPresent()) && !visible && !toastDismissed
      ? Optional.of({ kind: drafts.isPresent() ? 'success' : 'failure' })
      : Optional.empty();

  const value: RuleExtractionTracking = {
    visible,
    extracting,
    failure,
    drafts,
    toast,
    openImport,
    closeImport,
    extract,
    retry,
    continueInBackground,
    dismissFailure,
    dismissToast,
    notifyPageMounted,
  };

  return (
    <RuleExtractionTrackingReactContext.Provider value={Optional.of(value)}>
      {children}
    </RuleExtractionTrackingReactContext.Provider>
  );
};
